import * as React from 'react';
import {
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IColumn,
  PrimaryButton,
  Dialog,
  DialogType,
  DialogFooter,
  DefaultButton,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Label,
  Dropdown,
  IDropdownOption,
  IDropdownStyles,
  DropdownMenuItemType
} from '@fluentui/react';
import { Utility, telemetryHook } from '../../shared/models/Helper';
import { UIConstants } from '../../shared/models/Constants';
import AccessAPI from '../../store/AccessAPI';
import ITenant from '../../shared/models/Tenant.model';

interface HierarchyUpdatesProps {
  match: any;
  history: any;
  location: any;
}

interface HierarchyUpdateRun {
  id: string;
  startOfRun: string;
  endOfRun: string | null;
  status: string;
  typeOfUpdate: string;
  currentStep: string;
  countOfUpdatedRoleAssignments: number;
  countOfFailedUpdates: number;
}

interface Subsidiary {
  id: string;
  name: string;
}

interface Business {
  id: string;
  name: string;
}

interface HierarchyUpdatesState {
  hierarchyRuns: HierarchyUpdateRun[];
  isLoading: boolean;
  errorMessage: string;
  hideConfirmDialog: boolean;
  hideSuccessDialog: boolean;
  successMessage: string;
  processingAction: boolean;
  subsidiaries: Subsidiary[];
  selectedSubsidiaries: number[];
  businesses: Business[];
  selectedBusinesses: number[];
  isLoadingSubsidiaries: boolean;
  isLoadingBusinesses: boolean;
  subsidiaryOptions: IDropdownOption[];
  businessOptions: IDropdownOption[];
}

export class HierarchyUpdates extends React.Component<HierarchyUpdatesProps, HierarchyUpdatesState> {
  private tenantName: string;
  private tenantData: ITenant;

  constructor(props: HierarchyUpdatesProps) {
    super(props);
    this.state = {
      hierarchyRuns: [],
      isLoading: true,
      errorMessage: '',
      hideConfirmDialog: true,
      hideSuccessDialog: true,
      successMessage: '',
      processingAction: false,
      subsidiaries: [],
      selectedSubsidiaries: [],
      businesses: [],
      selectedBusinesses: [],
      isLoadingSubsidiaries: false,
      isLoadingBusinesses: false,
      subsidiaryOptions: [],
      businessOptions: [],
    };
  }

  componentDidMount() {
    this.tenantName = (this.props.match.params as any).tenantName;
    const filteredTenants = Utility.GetTenantData()
      .filter(x => x.TenantName.toLowerCase() === this.tenantName.toLowerCase());
    this.tenantData = filteredTenants.length > 0 ? filteredTenants[0] : null;

    this.fetchHierarchyRuns();
    this.fetchSubsidiaries();
    this.fetchBusinesses();
  }

  fetchHierarchyRuns = async () => {
    try {
      this.setState({ isLoading: true });
      const response = await AccessAPI.getHierarchyUpdateRuns(this.tenantData.TenantId);
      this.setState({
        hierarchyRuns: response.Data || [],
        isLoading: false,
        errorMessage: ''
      });
    } catch (error) {
      console.error('Error fetching hierarchy syncs:', error);
      this.setState({
        isLoading: false,
        errorMessage: 'Failed to load hierarchy update syncs. Please try again later.'
      });
    }
  };

  fetchSubsidiaries = async () => {
    try {
      this.setState({ isLoadingSubsidiaries: true });
      const response = await AccessAPI.getSubsidiaries();

      const options: IDropdownOption[] = [];

      for (const group of response) {
        options.push({
          key: group.subsidiaryGroupID,
          text: group.subsidiaryGroupName,
          itemType: DropdownMenuItemType.Header 
        });

        for (const option of group.subsidiaries) {
          options.push({
            key: option.subsidiaryId,
            text: option.subsidiaryName
          });
        }
      }
      
      this.setState({ 
        subsidiaries: response || [], 
        subsidiaryOptions: options,
        isLoadingSubsidiaries: false 
      });
    } catch (error) {
      console.error('Error fetching subsidiaries:', error);
      this.setState({ 
        isLoadingSubsidiaries: false, 
        errorMessage: 'Failed to load subsidiaries.',
        subsidiaryOptions: []
      });
    }
  };

  fetchBusinesses = async () => {
    try {
      this.setState({ isLoadingBusinesses: true });
      const response = await AccessAPI.getBusiness();

      const options: IDropdownOption[] = [];

      for (const group of response) {
        options.push({
          key: group.businessGroupID,
          text: group.businessGroupName,
          itemType: DropdownMenuItemType.Header
        });

        for (const option of group.businesses) {
          options.push({
            key: option.businessID,
            text: option.businessName
          });
        }
      }
      
      this.setState({ 
        businesses: response || [], 
        businessOptions: options,
        isLoadingBusinesses: false 
      });
    } catch (error) {
      console.error('Error fetching businesses:', error);
      this.setState({ 
        isLoadingBusinesses: false, 
        errorMessage: 'Failed to load businesses.',
        businessOptions: []
      });
    }
  };

  initiateNewRun = () => {
    this.setState({
      hideConfirmDialog: false,
    });
  };

  startNewRun = async () => {
    this.setState({
      hideConfirmDialog: true,
      processingAction: true
    });

    const { selectedSubsidiaries, selectedBusinesses } = this.state;
    const response = await AccessAPI.startHierarchyUpdateRun(
      this.tenantData.TenantId,
      selectedSubsidiaries,
      selectedBusinesses
    );

    if (response.IsSuccess) {
      this.setState({
        successMessage: 'New hierarchy sync has been successfully initiated.',
        hideSuccessDialog: false,
        processingAction: false,
        selectedBusinesses: [],
        selectedSubsidiaries: [],
      });
      await this.fetchHierarchyRuns();
    } else {
      this.setState({
        errorMessage: response.Error ? response.Error : 'Failed to start a new hierarchy sync.',
        processingAction: false
      });
    }
  };

  closeConfirmDialog = () => {
    this.setState({ hideConfirmDialog: true, selectedBusinesses: [], selectedSubsidiaries: [] });
  };

  closeSuccessDialog = () => {
    this.setState({ hideSuccessDialog: true, selectedBusinesses: [], selectedSubsidiaries: [] });
  };

  formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  onSubsidiariesChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
    if (!option) return;

    const key = option.key as number;

    this.setState(prevState => {
      let newSelectedSubsidiaries: number[];
      if (option.selected) {
        newSelectedSubsidiaries = [...prevState.selectedSubsidiaries, key];
      } else {
        newSelectedSubsidiaries = prevState.selectedSubsidiaries.filter(itemKey => itemKey !== key);
      }

      return {
        ...prevState, 
        selectedSubsidiaries: newSelectedSubsidiaries
      };
    });
  };

  onBusinessesChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
    if (!option) return;

    const key = option.key as number;

    this.setState(prevState => {
      let newSelectedBusinesses: number[];
      if (option.selected) {
        newSelectedBusinesses = [...prevState.selectedBusinesses, key];
      } else {
        newSelectedBusinesses = prevState.selectedBusinesses.filter(itemKey => itemKey !== key);
      }

      return {
        ...prevState,
        selectedBusinesses: newSelectedBusinesses
      };
    });
  };

  render() {
    const {
      hierarchyRuns,
      isLoading,
      errorMessage,
      hideConfirmDialog,
      hideSuccessDialog,
      successMessage,
      processingAction,
      selectedSubsidiaries,
      selectedBusinesses,
      isLoadingSubsidiaries,
      isLoadingBusinesses,
      subsidiaryOptions,
      businessOptions
    } = this.state;

    const dropdownStyles: Partial<IDropdownStyles> = {
      dropdown: { width: '100%', marginBottom: '10px' },
    };

    const columns: IColumn[] = [
      {
        key: 'startOfRun',
        name: 'Start Time',
        fieldName: 'startOfRun',
        minWidth: 150,
        isResizable: true,
        onRender: (item: HierarchyUpdateRun) => this.formatDate(item.startOfRun)
      },
      {
        key: 'endOfRun',
        name: 'End Time',
        fieldName: 'endOfRun',
        minWidth: 150,
        isResizable: true,
        onRender: (item: HierarchyUpdateRun) => this.formatDate(item.endOfRun)
      },
      {
        key: 'status',
        name: 'Status',
        fieldName: 'status',
        minWidth: 100,
        isResizable: true,
        onRender: (item: HierarchyUpdateRun) => (
          <span>
            {item.status}
          </span>
        )
      },
      {
        key: 'currentStep',
        name: 'Current Step',
        fieldName: 'currentStep',
        minWidth: 120,
        isResizable: true
      },
      {
        key: 'countOfUpdatedRoleAssignments',
        name: 'Updated Roles',
        fieldName: 'countOfUpdatedRoleAssignments',
        minWidth: 100,
        isResizable: true
      },
      {
        key: 'countOfFailedUpdates',
        name: 'Failed Updates',
        fieldName: 'countOfFailedUpdates',
        minWidth: 100,
        isResizable: true
      }
    ];
    
    return (
      <div className="ms-Grid-row hierarchy-updates-container" style={{
        margin: '20px 24px 0 24px',
        height: '100vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="ms-Grid-col ms-sm12" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Label style={{ paddingLeft: 10, fontSize: 20, marginTop: 10, marginBottom: 15 }}>
            <h1 className="h1" style={{ paddingLeft: 0, fontSize: 20, margin: 0 }}>
              {UIConstants.PageTitle.HierarchyUpdates}
            </h1>
          </Label>

          {errorMessage && (
            <MessageBar messageBarType={MessageBarType.error}>
              {errorMessage}
            </MessageBar>
          )}

          <div className="action-bar" style={{ padding: '10px 0' }}>
            <PrimaryButton
              text="Sync Business/Subsidiary for New/Remap/Rename"
              onClick={this.initiateNewRun}
              disabled={processingAction || isLoading || hierarchyRuns.some(run => run.status === 'In Progress')}
              className="create-run-button"
            />
            {processingAction && (
              <Spinner size={SpinnerSize.medium} label="Creating new sync..." />
            )}
          </div>
          {isLoading && !hierarchyRuns.length ? (
            <Spinner size={SpinnerSize.large} label="Loading hierarchy syncs..." />
          ) : (
            <DetailsList
              items={hierarchyRuns}
              columns={columns}
              selectionMode={SelectionMode.none}
              layoutMode={DetailsListLayoutMode.justified}
              isHeaderVisible={true}
            />
          )}

          <Dialog
            hidden={hideConfirmDialog}
            onDismiss={this.closeConfirmDialog}
            dialogContentProps={{
              type: DialogType.normal,
              title: 'Confirm New Hierarchy Sync',
              subText: 'Are you sure you want to start a new hierarchy sync? This process will update role assignments based on hierarchy changes. Please select the subsidiaries and businesses that need to be REMOVED as part of the sync.'
            }}
            modalProps={{
              isBlocking: true,
              styles: { main: { minWidth: 500 } }
            }}
          >
            {(isLoadingSubsidiaries || isLoadingBusinesses) && 
              <Spinner label="Loading options..." size={SpinnerSize.medium} />
            }
            
            {!(isLoadingSubsidiaries || isLoadingBusinesses) &&
              <div className="dropdown-lists" style={{ display: 'flex', flexDirection: 'column', padding: '10px 0' }}>
                <div style={{ marginBottom: '20px' }}>
                  <Label>Subsidiaries to REMOVE</Label>
                  {subsidiaryOptions.length > 0 ? (
                    <Dropdown
                      placeholder="Select subsidiaries"
                      multiSelect
                      options={subsidiaryOptions}
                      selectedKeys={selectedSubsidiaries}
                      onChange={this.onSubsidiariesChange}
                      styles={dropdownStyles}
                      disabled={processingAction}
                    />
                  ) : (
                    <MessageBar messageBarType={MessageBarType.info}>No subsidiaries found.</MessageBar>
                  )}
                </div>
                
                <div>
                  <Label>Businesses to REMOVE</Label>
                  {businessOptions.length > 0 ? (
                    <Dropdown
                      placeholder="Select businesses"
                      multiSelect
                      options={businessOptions}
                      selectedKeys={selectedBusinesses}
                      onChange={this.onBusinessesChange}
                      styles={dropdownStyles}
                      disabled={processingAction}
                    />
                  ) : (
                    <MessageBar messageBarType={MessageBarType.info}>No businesses found.</MessageBar>
                  )}
                </div>
              </div>
            }
            
            <DialogFooter>
              <PrimaryButton 
                onClick={this.startNewRun} 
                text="Start Sync" 
                disabled={processingAction || isLoadingSubsidiaries || isLoadingBusinesses} 
              />
              <DefaultButton 
                onClick={this.closeConfirmDialog} 
                text="Cancel" 
                disabled={processingAction}
              />
            </DialogFooter>
          </Dialog>

          <Dialog
            hidden={hideSuccessDialog}
            onDismiss={this.closeSuccessDialog}
            dialogContentProps={{
              type: DialogType.normal,
              title: 'Success',
              subText: successMessage
            }}
          >
            <DialogFooter>
              <PrimaryButton onClick={this.closeSuccessDialog} text="OK" />
            </DialogFooter>
          </Dialog>
        </div>
      </div>
    );
  }
}

export default HierarchyUpdates;
